import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import {
    AlertCircle,
    ArrowRight,
    CheckCircle,
    ChevronDown,
    ChevronUp,
    Edit3,
    Eye,
    Loader2,
    Plus,
    Smartphone,
    Trash2,
    Upload,
    Wrench,
    X,
} from 'lucide-react';
import { API_ENDPOINTS } from '../../constants/api';
import { apiFetch } from '../../utils/apiClient';
import { MobileAutoTestStep } from './MobileAutoTestStep';
import { MobileInspectorModal } from './MobileInspectorModal';
import { loadJobResult, submitJobOperation, waitForJobResult } from '../../services/jobs';

// Appium Android locator strategies (mirrors WebdriverIO + Appium selector styles).
const STRATEGY_OPTIONS = [
    { value: 'accessibility-id', label: 'accessibility-id', placeholder: 'login_button' },
    { value: 'id', label: 'id (resource-id)', placeholder: 'com.app:id/login' },
    { value: 'xpath', label: 'xpath', placeholder: '//android.widget.Button[@text="Login"]' },
    { value: 'android-uiautomator', label: 'android-uiautomator', placeholder: 'new UiSelector().text("Login")' },
    { value: 'class-name', label: 'class-name', placeholder: 'android.widget.EditText' },
    { value: 'text', label: 'text', placeholder: 'Login' },
];

const STRATEGY_VALUES = STRATEGY_OPTIONS.map((option) => option.value);

const getStrategyPlaceholder = (strategy) =>
    STRATEGY_OPTIONS.find((option) => option.value === strategy)?.placeholder || 'Selector value';

const withoutTransientId = (value = {}) => {
    const { id: _id, ...rest } = value || {};
    return rest;
};

const buildMobilePOMDraftSignature = (draft = {}) => JSON.stringify({
    pageName: String(draft.pomName || ''),
    appPackage: String(draft.appPackage || ''),
    appActivity: String(draft.appActivity || ''),
    locators: (draft.locators || []).map(withoutTransientId),
    functions: (draft.functions || []).map(withoutTransientId),
});

const getMobilePOMValidationError = (draft = {}) => {
    const pomName = String(draft.pomName || '');
    const appPackage = String(draft.appPackage || '');
    const locators = Array.isArray(draft.locators) ? draft.locators : [];
    const functions = Array.isArray(draft.functions) ? draft.functions : [];

    if (!pomName.trim()) return 'POM name is required.';
    if (!appPackage.trim()) return 'App package is required (e.g. com.example.app).';
    if (locators.length === 0) return 'Add at least one locator before saving.';

    for (const locator of locators) {
        if (!String(locator?.name || '').trim()) return 'All locators must have a name.';
        if (!String(locator?.value || '').trim()) {
            return `Locator '${locator.name}' must include a selector value.`;
        }
    }
    const locatorNames = locators.map((locator) => String(locator?.name || '').trim()).filter(Boolean);
    if (new Set(locatorNames).size !== locatorNames.length) return 'Locator names must be unique.';

    for (const method of functions) {
        if (!String(method?.name || '').trim()) return 'All methods must have a name.';
        if (!String(method?.description || '').trim()) return 'All methods must have a description.';
        if (!String(method?.body || '').trim()) {
            return `Method '${method.name}' must include a body.`;
        }
    }
    const methodNames = functions.map((method) => String(method?.name || '').trim()).filter(Boolean);
    if (new Set(methodNames).size !== methodNames.length) return 'Method names must be unique.';
    return '';
};

const formatMobilePOMLocatorForAPI = (locator = {}) => ({
    name: locator.name,
    strategy: locator.strategy,
    value: locator.value,
    description: locator.description || '',
});

export const MobilePOMStep = ({
    pdfId,
    onBack,
    onNext,
    onInternalBackChange,
    editedTestCases = null,
    processingResult = null,
    onContinueAutomation = null,
    testingType = 'mobile',
    showBackButton = true,
}) => {
    const [viewMode, setViewMode] = useState('list');
    const [showLoginGate, setShowLoginGate] = useState(false);
    const [selectedPOMName, setSelectedPOMName] = useState(null);

    const [pomsList, setPomsList] = useState([]);
    const [loadingList, setLoadingList] = useState(false);
    const [listError, setListError] = useState(null);

    const [pomName, setPomName] = useState('');
    const [appPackage, setAppPackage] = useState('');
    const [appActivity, setAppActivity] = useState('');
    const [locators, setLocators] = useState([]);
    const [functions, setFunctions] = useState([]);
    // APK ingestion: upload the BRD's app, verify it matches, load real locators.
    const [apkInfo, setApkInfo] = useState(null); // { package, activity, label, filename }
    const [apkBusy, setApkBusy] = useState(false);
    const [apkStatus, setApkStatus] = useState(null); // { type: 'info'|'match'|'mismatch'|'error', message }
    const [originalPomName, setOriginalPomName] = useState(null);
    const [originalLocators, setOriginalLocators] = useState([]);
    const [originalFunctions, setOriginalFunctions] = useState([]);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [expandedLocator, setExpandedLocator] = useState(null);
    const [expandedFunction, setExpandedFunction] = useState(null);
    const [pendingDeletePom, setPendingDeletePom] = useState(null);
    const [inspectorOpen, setInspectorOpen] = useState(false);
    const [autosaveStatus, setAutosaveStatus] = useState('idle');
    const autosaveTimerRef = useRef(null);
    const saveQueueRef = useRef(Promise.resolve());
    const lastSavedSignatureRef = useRef('');
    const latestDraftRef = useRef(null);
    const flushPOMEditRef = useRef(() => Promise.resolve(null));

    const resetEditorState = () => {
        if (autosaveTimerRef.current) {
            window.clearTimeout(autosaveTimerRef.current);
            autosaveTimerRef.current = null;
        }
        setPomName('');
        setAppPackage('');
        setAppActivity('');
        setLocators([]);
        setFunctions([]);
        setSelectedPOMName(null);
        setOriginalPomName(null);
        setOriginalLocators([]);
        setOriginalFunctions([]);
        setExpandedLocator(null);
        setExpandedFunction(null);
        setError(null);
        setSuccess(null);
        setAutosaveStatus('idle');
        lastSavedSignatureRef.current = '';
    };

    useEffect(() => {
        if (!onInternalBackChange) return undefined;

        if (viewMode === 'list') {
            onInternalBackChange(false, null);
            return undefined;
        }

        onInternalBackChange(true, async () => {
            await flushPOMEditRef.current({ keepalive: false, silent: true });
            resetEditorState();
            setViewMode('list');
        });

        return () => onInternalBackChange(false, null);
    }, [viewMode, onInternalBackChange]);

    const fetchPOMsList = useCallback(async () => {
        if (!pdfId) {
            setListError('Missing pdf_id. Please upload/select a PDF first.');
            setPomsList([]);
            return;
        }

        setLoadingList(true);
        setListError(null);

        try {
            const response = await apiFetch(`${API_ENDPOINTS.MOBILE_POMS}?pdf_id=${encodeURIComponent(pdfId)}`, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
            });

            if (!response.ok) throw new Error('Failed to fetch mobile POMs');

            const result = await response.json();
            setPomsList(result.pages || []);
        } catch (err) {
            setListError(err.message || 'Unable to load mobile POM list.');
        } finally {
            setLoadingList(false);
        }
    }, [pdfId]);

    const fetchPOMDetails = useCallback(async (name) => {
        if (!pdfId) {
            setError('Missing pdf_id. Please upload/select a PDF first.');
            return;
        }

        setLoading(true);
        setError(null);
        setSuccess(null);

        try {
            const response = await apiFetch(`${API_ENDPOINTS.MOBILE_POMS}/${name}?pdf_id=${encodeURIComponent(pdfId)}`, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
            });

            if (!response.ok) throw new Error('Mobile POM not found');

            const result = await response.json();

            setPomName(result.pageName || '');
            setAppPackage(String(result.appPackage || '').trim());
            setAppActivity(String(result.appActivity || '').trim());
            setSelectedPOMName(name);
            setOriginalPomName(name);

            const mappedLocators = (result.locators || []).map((loc, idx) => ({
                id: Date.now() + idx,
                name: loc.name || '',
                strategy: STRATEGY_VALUES.includes(loc.strategy) ? loc.strategy : 'accessibility-id',
                value: loc.value || '',
                description: loc.description || '',
            }));

            const mappedFunctions = (result.functions || []).map((func, idx) => ({
                ...func,
                id: Date.now() + idx + 10000,
                returnType: func.returnType || 'Promise<void>',
            }));

            lastSavedSignatureRef.current = buildMobilePOMDraftSignature({
                pomName: result.pageName || '',
                appPackage: String(result.appPackage || '').trim(),
                appActivity: String(result.appActivity || '').trim(),
                locators: mappedLocators,
                functions: mappedFunctions,
            });
            setAutosaveStatus('saved');
            setLocators(mappedLocators);
            setFunctions(mappedFunctions);
            setOriginalLocators(mappedLocators.map((loc) => ({ ...loc })));
            setOriginalFunctions(mappedFunctions.map((func) => ({ ...func })));
        } catch (err) {
            setError(err.message || 'Unable to load mobile POM details.');
        } finally {
            setLoading(false);
        }
    }, [pdfId]);

    useEffect(() => {
        if (viewMode === 'list') {
            fetchPOMsList();
        }
    }, [viewMode, fetchPOMsList]);

    // Restore the BRD's uploaded APK on load (it's stored per-BRD on the server), so
    // navigating away and back keeps the "Target app" card + "Replace APK" instead of
    // resetting to an empty "Upload APK" state.
    useEffect(() => {
        if (!pdfId) return undefined;
        let cancelled = false;
        (async () => {
            try {
                const res = await apiFetch(`${API_ENDPOINTS.APK_STATUS}?pdf_id=${encodeURIComponent(pdfId)}`, {
                    method: 'GET',
                    headers: { 'Content-Type': 'application/json' },
                });
                if (!res.ok || cancelled) return;
                const data = await res.json();
                if (cancelled || !data?.exists) return;
                setApkInfo({
                    package: data.package,
                    activity: data.activity,
                    label: data.label,
                    filename: data.filename,
                });
                if (data.verified) {
                    setApkStatus({
                        type: 'match',
                        message: data.reason || "This BRD's app is uploaded and verified.",
                    });
                }
            } catch {
                /* ignore — leave the empty upload state */
            }
        })();
        return () => { cancelled = true; };
    }, [pdfId]);

    // Once the APK verifies against the BRD, drop the "upload the APK first" nag.
    useEffect(() => {
        if (apkStatus?.type === 'match') {
            setListError(null);
        }
    }, [apkStatus]);

    useEffect(() => {
        if (!pendingDeletePom) return undefined;

        const closeOnEscape = (event) => {
            if (event.key === 'Escape' && !loading) {
                setPendingDeletePom(null);
            }
        };

        window.addEventListener('keydown', closeOnEscape);
        return () => window.removeEventListener('keydown', closeOnEscape);
    }, [pendingDeletePom, loading]);

    const handleViewPOM = (name) => {
        setViewMode('view');
        fetchPOMDetails(name);
    };

    const handleEditPOM = (name) => {
        setViewMode('edit');
        fetchPOMDetails(name);
    };

    const handleCreateNew = () => {
        if (apkMismatched) {
            setListError('The uploaded APK does not match this BRD. Upload the correct app to create POMs.');
            return;
        }
        if (!apkVerified) {
            setListError("Upload this BRD's app (APK) above and confirm it matches before creating POMs.");
            return;
        }
        setListError(null);
        resetEditorState();
        // Prefill only the app identity from the verified APK; locators are left
        // EMPTY on purpose — you build them with "Pick from device" / manual Add.
        if (apkInfo) {
            setAppPackage(String(apkInfo.package || '').trim());
            if (apkInfo.activity) setAppActivity(String(apkInfo.activity).trim());
        }
        setViewMode('create');
    };

    const addLocator = () => {
        const newLocator = {
            id: Date.now(),
            name: '',
            strategy: 'accessibility-id',
            value: '',
            description: '',
        };
        setLocators((prev) => [...prev, newLocator]);
        setExpandedLocator(newLocator.id);
    };

    // Append a locator captured from the live device via the inspector.
    const addLocatorFromInspector = (picked) => {
        const newLocator = {
            id: Date.now() + Math.floor(Math.random() * 1000),
            name: picked.name || '',
            strategy: STRATEGY_VALUES.includes(picked.strategy) ? picked.strategy : 'accessibility-id',
            value: picked.value || '',
            description: '',
        };
        setLocators((prev) => [...prev, newLocator]);
        setExpandedLocator(newLocator.id);
    };

    const updateLocator = (id, field, value) => {
        setLocators((prev) => prev.map((loc) => (loc.id === id ? { ...loc, [field]: value } : loc)));
    };

    const deleteLocator = (id) => {
        setLocators((prev) => prev.filter((loc) => loc.id !== id));
    };

    // A compact description of the BRD (its test cases) so the backend can ask
    // the LLM whether the uploaded APK is actually this BRD's app.
    const buildBrdContext = useCallback(() => {
        const src = editedTestCases;
        const cases = Array.isArray(src)
            ? src
            : (src && typeof src === 'object')
                ? [...(src.positive || []), ...(src.negative || [])]
                : [];
        const lines = cases
            .slice(0, 25)
            .map((tc) => tc?.test_description || tc?.test_name || tc?.scenario || tc?.title || tc?.description || '')
            .filter(Boolean);
        return lines.join('\n').slice(0, 3500);
    }, [editedTestCases]);

    // Upload the BRD's APK ONCE on the automation page: install it on the emulator,
    // verify it matches the BRD, and stash its REAL locators. Only after a match can
    // POMs be created — each new POM is prefilled with this app + these locators.
    const handleApkUpload = async (file) => {
        if (!file) return;
        if (!pdfId) {
            setApkStatus({ type: 'error', message: 'No BRD context — reopen this section and try again.' });
            return;
        }
        setApkBusy(true);
        setListError(null); // clear any "upload the APK first" nag once the user acts
        setApkStatus({ type: 'info', message: `Reading ${file.name}…` });
        try {
            const form = new FormData();
            form.append('pdf_id', pdfId);
            form.append('file', file);
            const uploadRes = await apiFetch(API_ENDPOINTS.APK_UPLOAD, { method: 'POST', body: form });
            if (!uploadRes.ok) {
                const err = await uploadRes.json().catch(() => ({}));
                throw new Error(err.detail || 'APK upload failed.');
            }
            const info = await uploadRes.json();
            setApkInfo({ package: info.package, activity: info.activity, label: info.label, filename: info.filename });
            setApkStatus({ type: 'info', message: `Installing ${info.label || info.package} on the emulator and reading its screens…` });
            const { job } = await submitJobOperation(API_ENDPOINTS.APK_ANALYZE, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ pdf_id: pdfId, brd_context: buildBrdContext() }),
            }, { errorMessage: 'Could not queue APK analysis.' });
            const snapshot = await waitForJobResult(job);
            const result = await loadJobResult(snapshot.job);
            if (result.status === 'mismatch' || result.match === false) {
                setApkStatus({ type: 'mismatch', message: result.reason || 'The uploaded APK does not match this BRD.' });
                return;
            }
            setApkStatus({
                type: 'match',
                message: `${result.reason || 'APK verified.'} You can now create POMs — pick each screen's locators from the device.`,
            });
        } catch (err) {
            setApkStatus({ type: 'error', message: err.message || 'APK processing failed.' });
        } finally {
            setApkBusy(false);
        }
    };
    const apkVerified = apkStatus?.type === 'match';
    const apkMismatched = apkStatus?.type === 'mismatch';

    const addFunction = () => {
        const newFunction = {
            id: Date.now(),
            name: '',
            description: '',
            parameters: '',
            body: '',
            returnType: 'Promise<void>',
        };
        setFunctions((prev) => [...prev, newFunction]);
        setExpandedFunction(newFunction.id);
    };

    const updateFunction = (id, field, value) => {
        setFunctions((prev) => prev.map((func) => (func.id === id ? { ...func, [field]: value } : func)));
    };

    const deleteFunction = (id) => {
        setFunctions((prev) => prev.filter((func) => func.id !== id));
    };

    const validateForm = () => {
        const validationError = getMobilePOMValidationError({
            pomName,
            appPackage,
            appActivity,
            locators,
            functions,
        });
        if (!validationError) return true;
        setError(validationError);
        return false;
    };

    latestDraftRef.current = {
        viewMode,
        pdfId,
        pomName,
        appPackage,
        appActivity,
        locators,
        functions,
        originalPomName,
        originalLocators,
        originalFunctions,
    };

    const persistPOMEdit = useCallback((options = {}) => {
        if (autosaveTimerRef.current) {
            window.clearTimeout(autosaveTimerRef.current);
            autosaveTimerRef.current = null;
        }

        const runSave = async () => {
            const snapshot = latestDraftRef.current;
            if (
                snapshot?.viewMode !== 'edit'
                || !snapshot?.pdfId
                || !snapshot?.originalPomName
            ) {
                return null;
            }

            const validationError = getMobilePOMValidationError(snapshot);
            if (validationError) {
                setAutosaveStatus('waiting');
                if (!options.silent) setError(validationError);
                return null;
            }

            const signature = buildMobilePOMDraftSignature(snapshot);
            if (signature === lastSavedSignatureRef.current && !options.force) {
                setAutosaveStatus('saved');
                return { alreadySaved: true, pageName: snapshot.originalPomName };
            }

            setAutosaveStatus('saving');
            const deletedLocators = snapshot.originalLocators
                .filter((original) => !snapshot.locators.some((current) => current.name === original.name))
                .map((locator) => locator.name);
            const deletedFunctions = snapshot.originalFunctions
                .filter((original) => !snapshot.functions.some((current) => current.name === original.name))
                .map((method) => method.name);

            try {
                const response = await apiFetch(
                    `${API_ENDPOINTS.MOBILE_POM_UPDATE}/${encodeURIComponent(snapshot.originalPomName)}`,
                    {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            pdf_id: snapshot.pdfId,
                            pageName: snapshot.pomName,
                            appPackage: snapshot.appPackage.trim(),
                            appActivity: snapshot.appActivity.trim(),
                            locatorsToAdd: snapshot.locators.map(formatMobilePOMLocatorForAPI),
                            locatorsToDelete: deletedLocators,
                            functionsToAdd: snapshot.functions.map((method) => ({
                                name: method.name,
                                description: method.description,
                                parameters: method.parameters || '',
                                body: method.body,
                                returnType: method.returnType,
                            })),
                            functionsToDelete: deletedFunctions,
                        }),
                        keepalive: Boolean(options.keepalive),
                    }
                );

                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    throw new Error(errorData.detail || 'Failed to update mobile POM');
                }

                const result = await response.json();
                const nextOriginalName = result.pageName || snapshot.pomName;
                lastSavedSignatureRef.current = signature;
                latestDraftRef.current = {
                    ...latestDraftRef.current,
                    originalPomName: nextOriginalName,
                    originalLocators: snapshot.locators.map((locator) => ({ ...locator })),
                    originalFunctions: snapshot.functions.map((method) => ({ ...method })),
                };
                setOriginalPomName(nextOriginalName);
                setSelectedPOMName(nextOriginalName);
                setOriginalLocators(snapshot.locators.map((locator) => ({ ...locator })));
                setOriginalFunctions(snapshot.functions.map((method) => ({ ...method })));
                setAutosaveStatus(
                    buildMobilePOMDraftSignature(latestDraftRef.current) === signature
                        ? 'saved'
                        : 'pending'
                );
                return result;
            } catch (saveError) {
                setAutosaveStatus('error');
                if (!options.silent) {
                    setError(saveError.message || 'Failed to update mobile POM.');
                }
                throw saveError;
            }
        };

        const queuedSave = saveQueueRef.current.catch(() => null).then(runSave);
        saveQueueRef.current = queuedSave;
        return queuedSave;
    }, []);
    flushPOMEditRef.current = persistPOMEdit;

    useEffect(() => {
        if (autosaveTimerRef.current) {
            window.clearTimeout(autosaveTimerRef.current);
            autosaveTimerRef.current = null;
        }
        if (viewMode !== 'edit' || !pdfId || !originalPomName) return undefined;

        const snapshot = latestDraftRef.current;
        const signature = buildMobilePOMDraftSignature(snapshot);
        if (signature === lastSavedSignatureRef.current) {
            setAutosaveStatus('saved');
            return undefined;
        }
        if (getMobilePOMValidationError(snapshot)) {
            setAutosaveStatus('waiting');
            return undefined;
        }

        setAutosaveStatus('pending');
        autosaveTimerRef.current = window.setTimeout(() => {
            persistPOMEdit({ silent: true }).catch(() => {});
        }, 800);

        return () => {
            if (autosaveTimerRef.current) {
                window.clearTimeout(autosaveTimerRef.current);
                autosaveTimerRef.current = null;
            }
        };
    }, [
        appActivity,
        appPackage,
        functions,
        locators,
        originalPomName,
        pdfId,
        persistPOMEdit,
        pomName,
        viewMode,
    ]);

    useEffect(() => {
        const flushPendingEdit = (keepalive) => {
            persistPOMEdit({ keepalive, silent: true }).catch(() => {});
        };
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'hidden') flushPendingEdit(true);
        };
        const handlePageHide = () => flushPendingEdit(true);

        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('pagehide', handlePageHide);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('pagehide', handlePageHide);
            flushPendingEdit(false);
        };
    }, [persistPOMEdit]);

    const handleSavePOM = async () => {
        setError(null);
        setSuccess(null);

        if (!validateForm()) return;

        setLoading(true);

        try {
            if (viewMode === 'edit') {
                const result = await persistPOMEdit({ force: false, silent: false });
                setSuccess(`Mobile POM '${pomName}' updated successfully.`);
                window.setTimeout(() => {
                    resetEditorState();
                    setViewMode('list');
                }, 500);
                return result;
            }

            const formattedLocators = locators.map(formatMobilePOMLocatorForAPI);
            const formattedFunctions = functions.map((func) => ({
                name: func.name,
                description: func.description,
                parameters: func.parameters || '',
                body: func.body,
                returnType: func.returnType,
            }));

            const payload = {
                pdf_id: pdfId,
                pageName: pomName,
                appPackage: appPackage.trim(),
                appActivity: appActivity.trim(),
                locators: formattedLocators,
                functions: formattedFunctions,
            };

            const response = await apiFetch(API_ENDPOINTS.MOBILE_POM_CREATE, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.detail || 'Failed to create mobile POM');
            }

            const result = await response.json();
            setSuccess(`Mobile POM '${pomName}' created successfully.`);

            window.setTimeout(() => {
                resetEditorState();
                setViewMode('list');
            }, 1000);
            return result;
        } catch (err) {
            setError(err.message || `Failed to ${viewMode === 'create' ? 'create' : 'update'} mobile POM.`);
        } finally {
            setLoading(false);
        }
    };

    const confirmDeletePOM = async () => {
        if (!pendingDeletePom) return;
        if (!pdfId) {
            setListError('Missing pdf_id. Please upload/select a PDF first.');
            return;
        }

        const name = pendingDeletePom;
        setLoading(true);
        setError(null);
        setListError(null);

        try {
            const response = await apiFetch(`${API_ENDPOINTS.MOBILE_POMS}/${name}?pdf_id=${encodeURIComponent(pdfId)}`, {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
            });

            if (!response.ok) throw new Error('Failed to delete mobile POM.');

            setPendingDeletePom(null);
            setSuccess(`Mobile POM '${name}' deleted successfully.`);
            fetchPOMsList();
        } catch (err) {
            setListError(err.message || 'Failed to delete mobile POM.');
        } finally {
            setLoading(false);
        }
    };

    const listStats = useMemo(() => {
        const totalPoms = pomsList.length;
        const totalLocators = pomsList.reduce((sum, pom) => sum + Number(pom.locators || 0), 0);
        const totalMethods = pomsList.reduce((sum, pom) => sum + Number(pom.functions || 0), 0);
        return { totalPoms, totalLocators, totalMethods };
    }, [pomsList]);

    const isReadOnly = viewMode === 'view';
    const isCreateView = viewMode === 'create';
    const hasSavedPOM = pomsList.length > 0 || Boolean(selectedPOMName);

    const modeTitle = isReadOnly
        ? 'View Mobile Page Object'
        : isCreateView
            ? 'Create Mobile Page Object'
            : 'Edit Mobile Page Object';

    const modeDescription = isReadOnly
        ? 'Review saved Android locators and methods in read-only mode.'
        : 'Define the Android app, locators, and methods for Appium automation.';

    const draftSummary = `${locators.length} locator${locators.length !== 1 ? 's' : ''} and ${functions.length} method${functions.length !== 1 ? 's' : ''}`;
    const autosaveMessage = {
        pending: 'Changes pending',
        saving: 'Saving...',
        saved: 'Saved to application',
        waiting: 'Complete required fields to save',
        error: 'Save failed',
    }[autosaveStatus] || '';

    const closeDeleteModal = () => {
        if (!loading) setPendingDeletePom(null);
    };

    const handleContinue = () => {
        if (!hasSavedPOM) {
            setError('Create and save at least one mobile POM before continuing.');
            return;
        }
        setError(null);
        setListError(null);
        // Mirror the web flow's "Does this app require login?" gate before
        // opening the automation (scripts) step.
        setShowLoginGate(true);
    };

    const proceedToMobileScripts = () => {
        setShowLoginGate(false);
        if (onNext) onNext();
    };

    const renderLoginGateModal = () => {
        if (!showLoginGate) return null;
        return ReactDOM.createPortal(
            <div className="modal-overlay p-3 md:p-5" onClick={() => setShowLoginGate(false)}>
                <div
                    className="modal-shell w-full max-w-lg overflow-hidden rounded-2xl bg-white"
                    onClick={(event) => event.stopPropagation()}
                >
                    <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-5 py-4">
                        <div className="flex items-center gap-2">
                            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-700">
                                <Smartphone className="h-5 w-5" />
                            </div>
                            <div>
                                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                                    Automation Login
                                </p>
                                <h2 className="text-lg font-bold text-slate-950">Does this app require login?</h2>
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={() => setShowLoginGate(false)}
                            className="secondary-btn h-9 w-9 shrink-0 px-0 py-0"
                            aria-label="Close login requirement prompt"
                        >
                            <X className="h-4 w-4" />
                        </button>
                    </div>

                    <div className="space-y-3 p-5">
                        <button
                            type="button"
                            onClick={proceedToMobileScripts}
                            className="flex w-full items-start gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-left transition hover:bg-emerald-100"
                        >
                            <span>
                                <span className="block text-sm font-semibold text-slate-900">No login required</span>
                                <span className="block text-xs leading-5 text-slate-600">
                                    Continue directly to Appium script generation and execution.
                                </span>
                            </span>
                        </button>

                        <button
                            type="button"
                            onClick={proceedToMobileScripts}
                            className="flex w-full items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-left transition hover:bg-blue-100"
                        >
                            <span>
                                <span className="block text-sm font-semibold text-slate-900">Uses app test login</span>
                                <span className="block text-xs leading-5 text-slate-600">
                                    Generated scripts sign in with the seeded test account automatically. Pick the run
                                    target (Emulator or BrowserStack) on the next screen.
                                </span>
                            </span>
                        </button>
                    </div>
                </div>
            </div>,
            document.body
        );
    };

    const renderDeleteModal = () => {
        if (!pendingDeletePom) return null;

        return ReactDOM.createPortal(
            <div className="modal-overlay" role="presentation" onClick={closeDeleteModal}>
                <div
                    className="modal-shell max-w-md"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="delete-mobile-pom-title"
                    onClick={(event) => event.stopPropagation()}
                >
                    <div className="border-b border-red-200 bg-gradient-to-r from-red-600 to-rose-600 px-5 py-4 text-white">
                        <div className="flex items-start justify-between gap-3">
                            <div>
                                <h4 id="delete-mobile-pom-title" className="text-lg font-semibold">Delete Mobile POM</h4>
                                <p className="mt-1 text-sm text-red-100">
                                    Remove <span className="font-semibold">{pendingDeletePom}</span>? This action cannot be undone.
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={closeDeleteModal}
                                disabled={loading}
                                className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-white/40 bg-white/10 text-white hover:bg-white/20 disabled:opacity-50"
                                aria-label="Close delete modal"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        </div>
                    </div>

                    <div className="flex justify-end gap-3 bg-white px-5 py-4">
                        <button
                            type="button"
                            onClick={closeDeleteModal}
                            disabled={loading}
                            className="secondary-btn px-4 py-2"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={confirmDeletePOM}
                            disabled={loading}
                            className="inline-flex items-center gap-2 rounded-xl bg-red-600 px-4 py-2 font-semibold text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                            Delete
                        </button>
                    </div>
                </div>
            </div>,
            document.body
        );
    };

    if (viewMode === 'autofill') {
        return (
            <MobileAutoTestStep
                pdfId={pdfId}
                editedTestCases={editedTestCases}
                processingResult={processingResult}
                testingType={testingType}
                onClose={() => {
                    setViewMode('list');
                    fetchPOMsList();
                }}
                onContinue={onContinueAutomation}
            />
        );
    }

    if (viewMode === 'list') {
        return (
            <div className="space-y-5">
                <section className="soft-card border-blue-200/70 bg-gradient-to-br from-blue-50 via-cyan-50 to-white p-4 md:p-5">
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                        <div className="flex items-start gap-3">
                            <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-blue-200 bg-white text-blue-700 shadow-sm">
                                <Smartphone className="h-5 w-5" />
                            </div>
                            <div>
                                <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-blue-700">
                                    Automation Assets
                                </p>
                                <h3 className="mt-1 text-lg font-bold text-slate-900">Page Object Models</h3>
                                <p className="mt-1 text-sm text-slate-600">
                                    Manage reusable pages before generating final automation scripts.
                                </p>
                            </div>
                        </div>

                        <div className="flex w-full flex-col gap-2 lg:w-auto lg:items-end">
                            <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:justify-end">
                                <button
                                    type="button"
                                    onClick={handleCreateNew}
                                    className="primary-btn h-9 w-full px-4 py-2 text-sm sm:w-auto"
                                >
                                    <Plus className="h-4 w-4" />
                                    Create POM Page
                                </button>
                                {showBackButton && onBack && (
                                    <button
                                        type="button"
                                        onClick={onBack}
                                        className="secondary-btn h-9 w-full px-4 py-2 text-sm sm:w-auto"
                                    >
                                        Back
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="mt-4 grid gap-3 sm:grid-cols-3">
                        <div className="rounded-xl border border-blue-200 bg-white/85 p-3">
                            <p className="text-[11px] font-semibold uppercase tracking-wide text-blue-700">POMs</p>
                            <p className="mt-1 text-2xl font-bold text-slate-900">{listStats.totalPoms}</p>
                        </div>
                        <div className="rounded-xl border border-indigo-200 bg-white/85 p-3">
                            <p className="text-[11px] font-semibold uppercase tracking-wide text-indigo-700">Total Locators</p>
                            <p className="mt-1 text-2xl font-bold text-slate-900">{listStats.totalLocators}</p>
                        </div>
                        <div className="rounded-xl border border-cyan-200 bg-white/85 p-3">
                            <p className="text-[11px] font-semibold uppercase tracking-wide text-cyan-700">Total Methods</p>
                            <p className="mt-1 text-2xl font-bold text-slate-900">{listStats.totalMethods}</p>
                        </div>
                    </div>
                </section>

                {/* Target app (APK) — uploaded ONCE for the whole automation flow.
                    Only after it verifies against the BRD can POMs be created. */}
                <section className="soft-card space-y-3 p-4">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                            <h4 className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                                <Smartphone className="h-4 w-4 text-emerald-700" /> Target app (APK)
                                {apkVerified && (
                                    <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-700">
                                        <CheckCircle className="h-3 w-3" /> Verified
                                    </span>
                                )}
                            </h4>
                            <p className="mt-0.5 text-xs text-slate-500">
                                {apkInfo
                                    ? <>{apkInfo.label ? `${apkInfo.label} · ` : ''}<span className="font-mono">{apkInfo.package}</span></>
                                    : 'Upload the app this BRD targets. It is installed on the emulator, verified against the BRD, and its real locators prefill every POM you create.'}
                            </p>
                        </div>
                        <label className={`inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-lg border px-4 py-2 text-sm font-semibold transition ${apkBusy ? 'cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400' : 'border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100'}`}>
                            <input
                                type="file"
                                accept=".apk,application/vnd.android.package-archive"
                                disabled={apkBusy}
                                onChange={(event) => { handleApkUpload(event.target.files?.[0]); event.target.value = ''; }}
                                className="hidden"
                            />
                            {apkBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                            {apkInfo ? 'Replace APK' : 'Upload APK'}
                        </label>
                    </div>

                    {apkStatus && (
                        <div className={`flex items-start gap-2 rounded-lg border p-3 text-sm ${
                            apkStatus.type === 'match'
                                ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                                : apkStatus.type === 'info'
                                    ? 'border-blue-200 bg-blue-50 text-blue-800'
                                    : 'border-red-200 bg-red-50 text-red-700'
                        }`}>
                            {apkStatus.type === 'match'
                                ? <CheckCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                                : apkStatus.type === 'info'
                                    ? <Loader2 className="mt-0.5 h-4 w-4 flex-shrink-0 animate-spin" />
                                    : <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />}
                            <div>
                                {apkStatus.type === 'mismatch' && <p className="font-semibold">BRD ↔ APK mismatch</p>}
                                <p className={apkStatus.type === 'mismatch' ? 'mt-0.5' : ''}>{apkStatus.message}</p>
                            </div>
                        </div>
                    )}
                </section>

                {listError && (
                    <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700 flex items-start gap-2">
                        <AlertCircle className="h-4 w-4 mt-0.5" />
                        <span>{listError}</span>
                    </div>
                )}

                {success && (
                    <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-700 flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 mt-0.5" />
                        <span>{success}</span>
                    </div>
                )}

                {loadingList ? (
                    <div className="soft-card-muted flex items-center justify-center py-12">
                        <Loader2 className="h-6 w-6 animate-spin text-emerald-600" />
                    </div>
                ) : pomsList.length === 0 ? (
                    <div className="soft-card-muted border-dashed border-slate-300 text-center py-10">
                        <Smartphone className="mx-auto h-10 w-10 text-slate-400" />
                        <h4 className="mt-3 text-base font-semibold text-slate-900">No mobile POMs created yet</h4>
                        <p className="mt-1 text-sm text-slate-600">
                            Start by creating an Android page object for this module.
                        </p>
                        <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
                            <button
                                type="button"
                                onClick={handleCreateNew}
                                className="primary-btn"
                            >
                                <Plus className="h-4 w-4" />
                                Create POM Page
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {pomsList.map((pom, idx) => (
                            <article
                                key={`${pom.pageName}-${idx}`}
                                className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm transition-shadow hover:shadow-[0_12px_30px_-24px_rgba(15,23,42,0.9)] md:p-4"
                            >
                                <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                                    <button
                                        type="button"
                                        onClick={() => handleViewPOM(pom.pageName)}
                                        className="flex min-w-0 flex-1 flex-col gap-2 rounded-lg text-left transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                                        aria-label={`View ${pom.pageName}`}
                                    >
                                        <h4 className="truncate text-base font-semibold text-slate-900">{pom.pageName}</h4>
                                        <p className="truncate text-sm text-slate-600">
                                            {pom.appPackage || 'App package not set'}
                                        </p>
                                        <div className="flex flex-wrap gap-2 text-xs">
                                            <span className="rounded-full border border-blue-200 bg-blue-50 px-2.5 py-1 font-semibold text-blue-800">
                                                {pom.locators} locator{Number(pom.locators) === 1 ? '' : 's'}
                                            </span>
                                            <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 font-semibold text-emerald-800">
                                                {pom.functions} method{Number(pom.functions) === 1 ? '' : 's'}
                                            </span>
                                            {pom.generatedOn && (
                                                <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 font-medium text-slate-600">
                                                    Updated {new Date(pom.generatedOn).toLocaleString()}
                                                </span>
                                            )}
                                        </div>
                                    </button>

                                    <div className="grid shrink-0 grid-cols-3 gap-2 md:w-56">
                                        <button
                                            type="button"
                                            onClick={() => handleViewPOM(pom.pageName)}
                                            className="secondary-btn px-3 py-2 text-sm"
                                        >
                                            <Eye className="h-4 w-4" />
                                            View
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => handleEditPOM(pom.pageName)}
                                            className="secondary-btn px-3 py-2 text-sm"
                                        >
                                            <Edit3 className="h-4 w-4" />
                                            Edit
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setPendingDeletePom(pom.pageName)}
                                            className="inline-flex items-center justify-center gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm font-semibold text-red-700 transition hover:bg-red-100"
                                        >
                                            <Trash2 className="h-4 w-4" />
                                            Delete
                                        </button>
                                    </div>
                                </div>
                            </article>
                        ))}
                    </div>
                )}

                {onNext && pomsList.length > 0 && (
                    <section className="rounded-xl border border-slate-200 bg-white px-4 py-3">
                        <div className="flex justify-end">
                            <button
                                type="button"
                                onClick={handleContinue}
                                disabled={apkMismatched}
                                title={apkMismatched ? 'Resolve the BRD/APK mismatch before continuing.' : undefined}
                                className="primary-btn h-9 px-4 py-2 text-sm bg-gradient-to-r from-indigo-600 to-blue-600 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                Continue to Automation
                                <ArrowRight className="h-4 w-4" />
                            </button>
                        </div>
                    </section>
                )}

                {renderLoginGateModal()}
                {renderDeleteModal()}
            </div>
        );
    }

    return (
        <div className="space-y-5">
            <section className="soft-card border-emerald-200/70 bg-gradient-to-br from-emerald-50 via-green-50 to-white p-4 md:p-5">
                <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                    <div className="flex items-start gap-3">
                        <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-emerald-200 bg-white text-emerald-700 shadow-sm">
                            <Smartphone className="h-5 w-5" />
                        </div>
                        <div>
                            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-emerald-700">
                                Mobile Automation Builder
                            </p>
                            <h3 className="mt-1 text-lg font-bold text-slate-900">{modeTitle}</h3>
                            <p className="mt-1 text-sm text-slate-600">{modeDescription}</p>
                        </div>
                    </div>

                    <div className="rounded-xl border border-emerald-200 bg-white/90 px-3 py-2 text-sm text-slate-700">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700">Current Draft</p>
                        <p className="mt-1 font-semibold text-slate-900">{draftSummary}</p>
                        {(selectedPOMName || pomName) && (
                            <p className="mt-1 text-xs text-slate-600">
                                {isReadOnly ? 'Viewing' : 'Working on'} {selectedPOMName || pomName}
                            </p>
                        )}
                        {viewMode === 'edit' && autosaveMessage && (
                            <p
                                className={`mt-1 text-xs font-semibold ${
                                    autosaveStatus === 'error' ? 'text-red-600' : 'text-emerald-700'
                                }`}
                            >
                                {autosaveMessage}
                            </p>
                        )}
                    </div>
                </div>
            </section>

            {error && (
                <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700 flex items-start gap-2">
                    <AlertCircle className="h-4 w-4 mt-0.5" />
                    <span>{error}</span>
                </div>
            )}

            {success && (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-700 flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 mt-0.5" />
                    <span>{success}</span>
                </div>
            )}

            {loading && (
                <div className="soft-card-muted flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-emerald-600" />
                </div>
            )}

            <section className="soft-card space-y-4">
                <div className="flex items-center gap-2">
                    <Smartphone className="h-4 w-4 text-emerald-700" />
                    <h4 className="text-sm font-semibold text-slate-900">App Details</h4>
                </div>

                <div className="grid gap-3 md:grid-cols-3">
                    <div>
                        <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">
                            Page Object Name <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            value={pomName}
                            onChange={(event) => setPomName(event.target.value)}
                            disabled={isReadOnly}
                            placeholder="LoginScreen"
                            className="text-input disabled:cursor-not-allowed disabled:bg-slate-100"
                        />
                        <p className="mt-1 text-xs text-slate-500">Use PascalCase naming.</p>
                    </div>

                    <div>
                        <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">
                            App Package <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            value={appPackage}
                            onChange={(event) => setAppPackage(event.target.value)}
                            disabled={isReadOnly}
                            placeholder="com.example.app"
                            className="text-input disabled:cursor-not-allowed disabled:bg-slate-100"
                        />
                        <p className="mt-1 text-xs text-slate-500">From the verified APK.</p>
                    </div>

                    <div>
                        <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">
                            App Activity
                        </label>
                        <input
                            type="text"
                            value={appActivity}
                            onChange={(event) => setAppActivity(event.target.value)}
                            disabled={isReadOnly}
                            placeholder=".MainActivity"
                            className="text-input disabled:cursor-not-allowed disabled:bg-slate-100"
                        />
                        <p className="mt-1 text-xs text-slate-500">Launch activity (optional).</p>
                    </div>
                </div>
            </section>

            <section className="soft-card space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <h4 className="text-sm font-semibold text-slate-900">
                        Locators <span className="text-slate-500">({locators.length})</span> <span className="text-red-500">*</span>
                    </h4>
                    {!isReadOnly && (
                        <div className="flex items-center gap-2">
                            <button type="button" onClick={() => setInspectorOpen(true)} className="primary-btn px-3 py-2 text-sm">
                                <Smartphone className="h-4 w-4" />
                                Pick from device
                            </button>
                            <button type="button" onClick={addLocator} className="secondary-btn px-3 py-2 text-sm">
                                <Plus className="h-4 w-4" />
                                Add Locator
                            </button>
                        </div>
                    )}
                </div>

                {locators.length === 0 && (
                    <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 px-3 py-6 text-center text-sm text-slate-600">
                        No locators added yet.
                    </div>
                )}

                <div className="space-y-2">
                    {locators.map((loc) => {
                        const isExpanded = expandedLocator === loc.id;
                        return (
                            <article key={loc.id} className="overflow-hidden rounded-xl border border-slate-200 bg-white">
                                <button
                                    type="button"
                                    onClick={() => setExpandedLocator(isExpanded ? null : loc.id)}
                                    className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-slate-50"
                                >
                                    <div className="min-w-0">
                                        <p className="truncate text-sm font-semibold text-slate-900">{loc.name || 'Unnamed locator'}</p>
                                        <p className="mt-0.5 truncate text-xs text-slate-500">
                                            {loc.strategy} • {loc.value || 'No value yet'}
                                        </p>
                                    </div>
                                    {isExpanded ? (
                                        <ChevronUp className="h-4 w-4 text-slate-500" />
                                    ) : (
                                        <ChevronDown className="h-4 w-4 text-slate-500" />
                                    )}
                                </button>

                                {isExpanded && (
                                    <div className="space-y-3 border-t border-slate-200 bg-slate-50/80 p-4">
                                        <div className="grid gap-3 md:grid-cols-2">
                                            <div>
                                                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">
                                                    Name <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    value={loc.name}
                                                    onChange={(event) => updateLocator(loc.id, 'name', event.target.value)}
                                                    disabled={isReadOnly}
                                                    placeholder="loginButton"
                                                    className="text-input disabled:cursor-not-allowed disabled:bg-slate-100"
                                                />
                                            </div>

                                            <div>
                                                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">
                                                    Strategy <span className="text-red-500">*</span>
                                                </label>
                                                <select
                                                    value={loc.strategy}
                                                    onChange={(event) => updateLocator(loc.id, 'strategy', event.target.value)}
                                                    disabled={isReadOnly}
                                                    className="text-input disabled:cursor-not-allowed disabled:bg-slate-100"
                                                >
                                                    {STRATEGY_OPTIONS.map((option) => (
                                                        <option key={option.value} value={option.value}>
                                                            {option.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>

                                        <div>
                                            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">
                                                Value <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                value={loc.value}
                                                onChange={(event) => updateLocator(loc.id, 'value', event.target.value)}
                                                disabled={isReadOnly}
                                                placeholder={getStrategyPlaceholder(loc.strategy)}
                                                className="text-input disabled:cursor-not-allowed disabled:bg-slate-100"
                                            />
                                        </div>

                                        <div>
                                            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">
                                                Description
                                            </label>
                                            <input
                                                type="text"
                                                value={loc.description}
                                                onChange={(event) => updateLocator(loc.id, 'description', event.target.value)}
                                                disabled={isReadOnly}
                                                placeholder="Primary login button"
                                                className="text-input disabled:cursor-not-allowed disabled:bg-slate-100"
                                            />
                                        </div>

                                        {!isReadOnly && (
                                            <button
                                                type="button"
                                                onClick={() => deleteLocator(loc.id)}
                                                className="inline-flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-semibold text-red-700 hover:bg-red-100"
                                            >
                                                <Trash2 className="h-4 w-4" />
                                                Delete Locator
                                            </button>
                                        )}
                                    </div>
                                )}
                            </article>
                        );
                    })}
                </div>
            </section>

            <section className="soft-card space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <h4 className="text-sm font-semibold text-slate-900">
                        Methods <span className="text-slate-500">({functions.length})</span>
                    </h4>
                    {!isReadOnly && (
                        <button type="button" onClick={addFunction} className="secondary-btn px-3 py-2 text-sm">
                            <Plus className="h-4 w-4" />
                            Add Method
                        </button>
                    )}
                </div>

                {functions.length === 0 && (
                    <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 px-3 py-6 text-center text-sm text-slate-600">
                        No methods added yet.
                    </div>
                )}

                <div className="space-y-2">
                    {functions.map((func) => {
                        const isExpanded = expandedFunction === func.id;
                        return (
                            <article key={func.id} className="overflow-hidden rounded-xl border border-slate-200 bg-white">
                                <button
                                    type="button"
                                    onClick={() => setExpandedFunction(isExpanded ? null : func.id)}
                                    className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-slate-50"
                                >
                                    <div className="min-w-0">
                                        <p className="truncate text-sm font-semibold text-slate-900">{func.name || 'Unnamed method'}</p>
                                        <p className="mt-0.5 truncate text-xs text-slate-500">{func.description || 'No description yet'}</p>
                                    </div>
                                    {isExpanded ? (
                                        <ChevronUp className="h-4 w-4 text-slate-500" />
                                    ) : (
                                        <ChevronDown className="h-4 w-4 text-slate-500" />
                                    )}
                                </button>

                                {isExpanded && (
                                    <div className="space-y-3 border-t border-slate-200 bg-slate-50/80 p-4">
                                        <div className="grid gap-3 md:grid-cols-2">
                                            <div>
                                                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">
                                                    Name <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    value={func.name}
                                                    onChange={(event) => updateFunction(func.id, 'name', event.target.value)}
                                                    disabled={isReadOnly}
                                                    placeholder="login"
                                                    className="text-input disabled:cursor-not-allowed disabled:bg-slate-100"
                                                />
                                            </div>
                                            <div>
                                                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">
                                                    Return Type
                                                </label>
                                                <input
                                                    type="text"
                                                    value={func.returnType}
                                                    onChange={(event) => updateFunction(func.id, 'returnType', event.target.value)}
                                                    disabled={isReadOnly}
                                                    placeholder="Promise<void>"
                                                    className="text-input disabled:cursor-not-allowed disabled:bg-slate-100"
                                                />
                                            </div>
                                        </div>

                                        <div>
                                            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">
                                                Description <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                value={func.description}
                                                onChange={(event) => updateFunction(func.id, 'description', event.target.value)}
                                                disabled={isReadOnly}
                                                placeholder="Login with username and password"
                                                className="text-input disabled:cursor-not-allowed disabled:bg-slate-100"
                                            />
                                        </div>

                                        <div>
                                            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">
                                                Parameters
                                            </label>
                                            <input
                                                type="text"
                                                value={func.parameters}
                                                onChange={(event) => updateFunction(func.id, 'parameters', event.target.value)}
                                                disabled={isReadOnly}
                                                placeholder="username: string, password: string"
                                                className="text-input disabled:cursor-not-allowed disabled:bg-slate-100"
                                            />
                                            <p className="mt-1 text-xs text-slate-500">Comma-separated parameter signatures.</p>
                                        </div>

                                        <div>
                                            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">
                                                Body <span className="text-red-500">*</span>
                                            </label>
                                            <textarea
                                                value={func.body}
                                                onChange={(event) => updateFunction(func.id, 'body', event.target.value)}
                                                disabled={isReadOnly}
                                                rows={5}
                                                placeholder="await this.loginButton.click();"
                                                className="text-input font-mono disabled:cursor-not-allowed disabled:bg-slate-100"
                                            />
                                        </div>

                                        {!isReadOnly && (
                                            <button
                                                type="button"
                                                onClick={() => deleteFunction(func.id)}
                                                className="inline-flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-semibold text-red-700 hover:bg-red-100"
                                            >
                                                <Trash2 className="h-4 w-4" />
                                                Delete Method
                                            </button>
                                        )}
                                    </div>
                                )}
                            </article>
                        );
                    })}
                </div>
            </section>

            <section className="rounded-xl border border-slate-200 bg-white px-4 py-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <button
                        type="button"
                        onClick={async () => {
                            await persistPOMEdit({ keepalive: false, silent: true }).catch(() => null);
                            resetEditorState();
                            setViewMode('list');
                        }}
                        className="secondary-btn"
                    >
                        Back to List
                    </button>

                    <div className="flex flex-wrap gap-2 sm:justify-end">
                        {!isReadOnly && (
                            <button
                                type="button"
                                onClick={handleSavePOM}
                                disabled={loading}
                                className="primary-btn bg-gradient-to-r from-emerald-600 to-green-600"
                            >
                                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wrench className="h-4 w-4" />}
                                {loading ? 'Saving...' : 'Save POM'}
                            </button>
                        )}

                        {onNext && (
                            <button
                                type="button"
                                onClick={handleContinue}
                                disabled={loading || !hasSavedPOM}
                                className="primary-btn bg-gradient-to-r from-teal-600 to-emerald-600 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                Continue to Mobile Scripts
                                <ArrowRight className="h-4 w-4" />
                            </button>
                        )}
                    </div>
                </div>
            </section>

            {renderDeleteModal()}
            {inspectorOpen && (
                <MobileInspectorModal
                    pdfId={pdfId}
                    appPackage={appPackage}
                    appActivity={appActivity}
                    onPick={addLocatorFromInspector}
                    onClose={() => setInspectorOpen(false)}
                />
            )}
        </div>
    );
};

export default MobilePOMStep;
